import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, XCircle } from "lucide-react";
import { ROUTES_CONFIG } from "src/constants/routesConfig";

interface ModalProps {
  purchaseOrderId: string;
  onClose?: () => void;
}

interface SuccessProps extends ModalProps {}

interface ErrorProps extends ModalProps {
  onRetry: () => void;
}

const Success: React.FC<SuccessProps> = ({ purchaseOrderId, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-3">
            <CheckCircle2 className="w-6 h-6 text-success-600" />
            <h2 className="text-lg font-semibold text-gray-900">Shipment Details Updated</h2>
          </div>
          {onClose && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
              &times;
            </button>
          )}
        </div>

        <div className="p-5 space-y-4">
          <div className="bg-success-50 border border-success-200 rounded-lg p-4 space-y-2">
            <p className="text-sm text-gray-700">Thank you for your submission</p>
            <p className="text-sm text-gray-700">
              The purchase order shipment details have been updated successfully{" "}
              <span className="font-semibold text-success-700">(ID: {purchaseOrderId})</span>
            </p>
            <p className="text-sm text-gray-700">We look forward to future collaboration</p>
          </div>

          {/* Home Button */}
          <div className="flex justify-center pt-2">
            <Link to={ROUTES_CONFIG.home.path}>
              <button className="px-6 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors text-sm font-medium">
                Home
              </button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const Error: React.FC<ErrorProps> = ({ onRetry, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-3">
            <XCircle className="w-6 h-6 text-error-600" />
            <h2 className="text-lg font-semibold text-gray-900">Update Failed</h2>
          </div>
          {onClose && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
              &times;
            </button>
          )}
        </div>

        <div className="p-5">
          <p className="text-sm text-gray-700 text-center mb-6">
            An error occurred while updating the shipment details. Please try again.
          </p>

          {/* Footer Button */}
          <div className="flex justify-center">
            <button
              className="px-6 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors text-sm font-medium"
              onClick={onRetry}
            >
              Retry Update
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const UpdatePurchaseOrderShipmentDetailsModals = {
  Success,
  Error
};

export { UpdatePurchaseOrderShipmentDetailsModals };
